import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Box,
  BoxProps,
  Flex,
  GridItem,
  Image,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { Button } from "@verivest/shared-components";
import { ImageModel } from "@verivest/shared-components/src/api/models";
import { VideoOrImage } from "@verivest/shared-components/src/components/common/VideoOrImage";
import React, { useEffect, useState } from "react";
import {
  AnimationTypes,
  ComponentTransition,
} from "react-component-transition";
import { ChevronLeftIcon } from "./Icons";

interface VeriImageCarouselProps extends BoxProps {
  images?: ImageModel[] | null;
  maxImagePreviewHeight?: number | string;
  opportunityId?: string;
  video?: string;
}

const defaultMaxImagePreviewHeight = 400;
const darkenedBg = {
  bg: "bg.darkest",
  opacity: 0.9,
} as const;

const arrowStyles = {
  borderRadius: 0,
  pos: "absolute",
  top: "50%",
  w: "auto",
  mt: "-22px",
  p: "16px",
  color: "red",
  fontWeight: "bold",
  fontSize: "18px",
  transition: "0.6s ease",
  userSelect: "none",
  bg: "rgba(255, 255, 255, 0.21);",
  _hover: {},
} as const;

enum slideDirections {
  slideLeft = "slideLeft",
  slideRight = "slideRight",
  slideUp = "slideUp",
  slideDown = "slideDown",
}

export const VeriImageCarousel = ({
  images,
  maxImagePreviewHeight = defaultMaxImagePreviewHeight,
  video,
  opportunityId,
  ...rest
}: VeriImageCarouselProps) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [enterSlideAnimation, setEnterSlideAnimation] =
    useState<slideDirections>(slideDirections.slideLeft);
  const [exitSlideAnimation, setExitSlideAnimation] = useState<slideDirections>(
    slideDirections.slideRight,
  );
  const [slides, setSlides] = useState<ImageModel[]>([]);

  useEffect(() => {
    const newSlides = images?.length ? [...images] : [];
    if (video) newSlides.unshift({ url: video } as ImageModel);
    setSlides(newSlides);
  }, [video]);

  if (!slides || slides.length === 0) return null;

  const slidesCount = slides.length;

  const prevSlide = () => {
    setEnterSlideAnimation(slideDirections.slideRight);
    setExitSlideAnimation(slideDirections.slideLeft);
    setCurrentSlideIndex((s) => (s === 0 ? slidesCount - 1 : s - 1));
  };
  const nextSlide = () => {
    setEnterSlideAnimation(slideDirections.slideLeft);
    setExitSlideAnimation(slideDirections.slideRight);
    setCurrentSlideIndex((s) => (s === slidesCount - 1 ? 0 : s + 1));
  };
  const setSlide = (slide: number) => {
    setExitSlideAnimation(slideDirections.slideDown);
    setEnterSlideAnimation(slideDirections.slideUp);
    setCurrentSlideIndex(slide);
  };

  const currentSlide = slides[currentSlideIndex];

  return (
    <Box {...rest}>
      <Box
        w="full"
        alignItems="center"
        justifyContent="left"
        position="relative"
      >
        {currentSlideIndex === 0 && video ? (
          <VideoOrImage video={video || ""} opportunityId={opportunityId} />
        ) : (
          <Flex pos="relative" w={"full"}>
            <Box
              className={"sm:h-[400px]"}
              overflow={"hidden"}
              w="full"
              justifyContent="center"
              alignItems="center"
            >
              <ComponentTransition
                enterAnimation={AnimationTypes[enterSlideAnimation].enter}
                exitAnimation={AnimationTypes[exitSlideAnimation].exit}
              >
                <Box key={`slide-${currentSlideIndex}`} shadow="md">
                  <Image
                    w={"100%"}
                    src={currentSlide.url || ""}
                    boxSize="full"
                  />
                  {currentSlide.description ? (
                    <Box pos="absolute" bottom="24px" textAlign="center">
                      <Text
                        color="text.ink"
                        bg="bg.lighter"
                        opacity="0.8"
                        borderRadius="lg"
                        m={{ base: "2", md: "4" }}
                        p={{ base: "2", md: "4" }}
                      >
                        {currentSlide.description}
                      </Text>
                    </Box>
                  ) : null}
                </Box>
              </ComponentTransition>
            </Box>
          </Flex>
        )}
        <Button empty {...arrowStyles} left="10px" onClick={prevSlide}>
          <ChevronLeftIcon boxSize="26px" color={"white"} />
        </Button>
        <Button empty {...arrowStyles} right="10px" onClick={nextSlide}>
          <ChevronRightIcon boxSize="26px" color={"white"} />
        </Button>
      </Box>
      {slidesCount > 1 && (
        <SimpleGrid columns={4} spacing={1} mt={1}>
          {video && video?.length > 0 && (
            <GridItem
              onClick={() => {
                setSlide(0);
              }}
            >
              <Box position="relative">
                <VideoOrImage
                  video={video || ""}
                  image={""}
                  opportunityId={String(opportunityId)}
                  isDisabled={true}
                />
              </Box>
            </GridItem>
          )}
          {images?.map((image, sid) => (
            <Flex
              key={`slide-${sid}`}
              maxH={"120px"}
              onClick={() => {
                setSlide(video ? sid + 1 : sid);
              }}
              width="content"
              alignItems="center"
              justifyContent="center"
              overflow={"hidden"}
              pos="relative"
            >
              <Image src={image.url || ""} />
              <Box
                pos="absolute"
                bottom="0"
                left="0"
                right="0"
                top="0"
                bg="bg.darkest"
                opacity="0.5"
                _hover={{
                  ...darkenedBg,
                  transition: "0.3s ease",
                  cursor: "pointer",
                }}
              ></Box>
            </Flex>
          ))}
        </SimpleGrid>
      )}
    </Box>
  );
};
